import React, { useState, useEffect } from 'react';
import { Table, Button, Modal, Form, Input, Select, Pagination, Row, Col, Alert, Space, Spin } from 'antd';
import { FaEdit, FaTrashAlt, FaPlus } from 'react-icons/fa';
import axios from 'axios';
import './ProductsPage.css';

function ProductsPage() {
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [productsData, setProductsData] = useState([]);
  const [categories, setCategories] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(5);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [addModalError, setAddModalError] = useState('');
  const [addModalSuccess, setAddModalSuccess] = useState('');
  const [editModalError, setEditModalError] = useState('');
  const [editModalSuccess, setEditModalSuccess] = useState('');
  const [addedQuantity, setAddedQuantity] = useState(0);
  const [loading, setLoading] = useState(false);
  const [addForm] = Form.useForm();
  const [editForm] = Form.useForm();

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [productsRes, categoriesRes] = await Promise.all([
          axios.get('https://agpipefitting.org.et/api/products'),
          axios.get('https://agpipefitting.org.et/api/categories')
        ]);
        setProductsData(productsRes.data);
        setCategories(categoriesRes.data);
      } catch (error) {
        console.error('Error fetching data:', error);
        setErrorMessage(error.response?.data?.message || 'Failed to load data.');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const columns = [
    {
      title: 'Product Name',
      dataIndex: 'type',
      key: 'type',
      sorter: (a, b) => a.type?.localeCompare(b.type),
    },
   {
  title: 'Category',
  key: 'category',
  render: (_, record) => {
    const category = categories.find(
      c => Number(c.id) === Number(record.category_id)
    );
    return category ? category.name : '-';
  },
},

    {
      title: 'Price',
      key: 'price',
      render: (_, record) => record.price ? `ETB ${parseFloat(record.price).toFixed(2)}` : '-',
      sorter: (a, b) => parseFloat(a.price || 0) - parseFloat(b.price || 0),
    },
    {
      title: 'Size',
      dataIndex: 'size',
      key: 'size',
    },
    {
      title: 'Quantity',
      dataIndex: 'quantity',
      key: 'quantity',
      sorter: (a, b) => a.quantity - b.quantity,
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button 
            shape="circle"
            icon={<FaEdit className="action-icon" />}
            onClick={() => handleShowEditModal(record.id)}
            className="edit-btn"
          />
          <Button 
            shape="circle"
            icon={<FaTrashAlt className="action-icon" />}
            onClick={() => handleDeleteProduct(record.id)}
            danger
            className="delete-btn"
          />
        </Space>
      ),
    },
  ];

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const filteredProducts = productsData.filter(product => {
    const searchLower = searchQuery.toLowerCase();
    return (
      product.type?.toLowerCase().includes(searchLower) ||
      product.description?.toLowerCase().includes(searchLower)
    );
  });
  const currentProducts = filteredProducts.slice(indexOfFirstItem, indexOfLastItem);

  const handleShowEditModal = (productId) => {
    const product = productsData.find(item => item.id === productId);
    setEditingProduct(product);
    
    editForm.setFieldsValue({
      type: product.type,
      category_id: product.category_id?.toString(),
      price: product.price ? parseFloat(product.price).toString() : '',
      size: product.size,
    });
    
    setAddedQuantity(0);
    setShowEditModal(true);
  };

  const handleAddProduct = async (values) => {
    setAddModalError('');
    setLoading(true);
    
    try {
      console.log('Adding product with values:', values);
      
      // Create FormData object
      const formData = new FormData();
      
      // Append required fields in the specified order
      formData.append('type', values.type); // Product Name
      formData.append('category_id', values.category_id); // Category
      
      // Price (optional)
      if (values.price && values.price.trim()) {
        const priceValue = parseFloat(values.price);
        if (!isNaN(priceValue)) {
          formData.append('price', priceValue.toFixed(2));
        }
      }
      
      formData.append('size', values.size); // Size
      formData.append('quantity', values.quantity); // Initial Quantity

      // Log FormData contents for debugging
      console.log('FormData contents:');
      for (let [key, value] of formData.entries()) {
        console.log(key, value);
      }

      const response = await axios.post(
        'https://agpipefitting.org.et/api/products', 
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          }
        }
      );

      console.log('Response:', response.data);

      // Update state with new product
      setProductsData(prevProducts => [...prevProducts, response.data.product]);
      
      setAddModalSuccess('Product added successfully!');
      
      // Reset form and close modal
      setTimeout(() => {
        addForm.resetFields();
        setShowAddModal(false);
        setSuccessMessage('Product added successfully!');
        setAddModalSuccess('');
      }, 1500);
    } catch (error) {
      console.error('Error adding product:', error);
      
      if (error.response) {
        const { data, status } = error.response;
        
        if (status === 422) {
          // Laravel validation errors
          if (data.details) {
            const errorMessages = Object.values(data.details).flat().join(', ');
            setAddModalError(`Validation Error: ${errorMessages}`);
          } else if (data.error === 'Validation failed' && data.details) {
            const errorMessages = Object.values(data.details).flat().join(', ');
            setAddModalError(`Validation Error: ${errorMessages}`);
          } else if (data.message) {
            setAddModalError(data.message);
          } else {
            setAddModalError('Validation failed. Please check your input.');
          }
        } else {
          setAddModalError(data?.message || `Server Error (${status})`);
        }
      } else if (error.request) {
        setAddModalError('Network error. Please check your connection.');
      } else {
        setAddModalError(error.message || 'Error adding product');
      }
    } finally {
      setLoading(false);
    }
  };

 const handleUpdateProduct = async (values) => {
  setEditModalError('');
  setLoading(true);

  try {
    // Calculate new total quantity
    const oldQty = Number(editingProduct.quantity) || 0;
    const addQty = Number(addedQuantity) || 0;
    const newTotalQuantity = oldQty + addQty;

    // Create FormData object
    const formData = new FormData();
    formData.append('_method', 'PUT'); // VERY IMPORTANT for Laravel
    formData.append('type', values.type);
    formData.append('category_id', parseInt(values.category_id)); // ensure integer

    // Price (optional)
    if (values.price && values.price.trim()) {
      const priceValue = parseFloat(values.price);
      if (!isNaN(priceValue)) {
        formData.append('price', priceValue.toFixed(2));
      }
    }

    formData.append('size', values.size);
    formData.append('quantity', newTotalQuantity.toString());

    console.log('Update FormData contents:');
    for (let [key, value] of formData.entries()) {
      console.log(key, value);
    }

    // ⚡ IMPORTANT: Use POST with _method=PUT for multipart
    const response = await axios.post(
      `https://agpipefitting.org.et/api/products/${editingProduct.id}`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );

    console.log('Update response:', response.data);

    // Update UI state with new product
    setProductsData(productsData.map(p => 
      p.id === editingProduct.id ? response.data.product : p
    ));

    setEditModalSuccess(`Product updated successfully! New total: ${response.data.product.quantity}`);

    setTimeout(() => {
      setShowEditModal(false);
      setEditingProduct(null);
      setEditModalSuccess('');
      setAddedQuantity(0); // reset added quantity
    }, 1500);
  } catch (error) {
    console.error('Error updating product:', error);

    if (error.response) {
      const { data, status } = error.response;

      if (status === 422 && (data.details || data.error === 'Validation failed')) {
        const errorMessages = data.details ? Object.values(data.details).flat().join(', ') : 'Validation failed';
        setEditModalError(`Validation Error: ${errorMessages}`);
      } else {
        setEditModalError(data?.message || 'Failed to update product');
      }
    } else {
      setEditModalError(error.message || 'Failed to update product');
    }
  } finally {
    setLoading(false);
  }
};


  const handleDeleteProduct = async (productId) => {
    if (window.confirm('Are you sure you want to delete this product?')) {
      try {
        await axios.delete(`https://agpipefitting.org.et/api/products/${productId}`);
        setProductsData(productsData.filter(item => item.id !== productId));
        setSuccessMessage('Product deleted successfully!');
      } catch (error) {
        setErrorMessage('Error deleting product. Please try again.');
      }
    }
  };

  const formItemLayout = {
    labelCol: { span: 24 },
    wrapperCol: { span: 24 },
  };

  // Price validation function
  const validatePrice = (_, value) => {
    if (!value || value.trim() === '') {
      return Promise.resolve();
    }
    
    const price = parseFloat(value);
    if (isNaN(price)) {
      return Promise.reject(new Error('Please enter a valid number'));
    }
    
    if (price < 0) {
      return Promise.reject(new Error('Price cannot be negative'));
    }
    
    return Promise.resolve();
  };

  return (
    <div className="modern-container">
      <div className="page-header">
        <h1 className="page-title">Product Management</h1>
        <Button 
          type="primary" 
          onClick={() => {
            addForm.resetFields();
            setShowAddModal(true);
          }}
          icon={<FaPlus className="btn-icon" />}
          className="add-product-btn"
        >
          Add Product
        </Button>
      </div>

      <div className="search-bar-container">
        <Input.Search
          placeholder="Search products..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="modern-search"
          enterButton
        />
      </div>

      <div className="alerts-container">
        {successMessage && (
          <Alert 
            message={successMessage} 
            type="success" 
            showIcon 
            closable 
            onClose={() => setSuccessMessage('')}
          />
        )}
        {errorMessage && (
          <Alert 
            message={errorMessage} 
            type="error" 
            showIcon 
            closable 
            onClose={() => setErrorMessage('')}
          />
        )}
      </div>

      <Spin spinning={loading}>
        <Table
          columns={columns}
          dataSource={currentProducts}
          pagination={false}
          rowKey="id"
          className="modern-table"
          bordered
          loading={loading}
        />
      </Spin>

      <Pagination
        current={currentPage}
        total={filteredProducts.length}
        pageSize={itemsPerPage}
        onChange={(page) => setCurrentPage(page)}
        className="modern-pagination"
        showSizeChanger={false}
      />

      {/* Add Product Modal */}
      <Modal
        title={<span className="modal-title">Add New Product</span>}
        open={showAddModal}
        onCancel={() => {
          setShowAddModal(false);
          addForm.resetFields();
        }}
        footer={null}
        className="modern-modal"
        width={600}
        destroyOnClose
      >
        <Form
          {...formItemLayout}
          form={addForm}
          onFinish={handleAddProduct}
          initialValues={{
            type: '',
            category_id: '',
            price: '',
            size: '',
            quantity: ''
          }}
        >
          {/* Product Name */}
          <Row gutter={24}>
            <Col span={24}>
              <Form.Item
                label="Product Name"
                name="type"
                rules={[{ required: true, message: 'Please enter product name' }]}
              >
                <Input
                  placeholder="Enter product name"
                  className="modern-input"
                />
              </Form.Item>
            </Col>
          </Row>

          {/* Category */}
          <Row gutter={24}>
            <Col span={24}>
              <Form.Item
                label="Category"
                name="category_id"
                rules={[{ required: true, message: 'Please select a category' }]}
              >
                <Select
                  placeholder="Select category"
                  className="modern-input"
                >
                  {categories.map(category => (
                    <Select.Option key={category.id} value={category.id.toString()}>
                      {category.name}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          {/* Price and Size in one row */}
          <Row gutter={24}>
            <Col span={12}>
              <Form.Item
                label="Price (ETB)"
                name="price"
                rules={[
                  { validator: validatePrice }
                ]}
              >
                <Input
                  placeholder="Enter price"
                  className="modern-input"
                  type="number"
                  step="0.01"
                  min="0"
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Size"
                name="size"
                rules={[{ required: true, message: 'Please enter product size' }]}
              >
                <Input
                  placeholder="Enter product size"
                  className="modern-input"
                />
              </Form.Item>
            </Col>
          </Row>

          {/* Initial Quantity */}
          <Row gutter={24}>
            <Col span={12}>
              <Form.Item
                label="Initial Quantity"
                name="quantity"
                rules={[
                  { required: true, message: 'Please enter initial quantity' },
                  { pattern: /^\d+$/, message: 'Please enter a valid number' }
                ]}
              >
                <Input
                  type="number"
                  min="0"
                  placeholder="Enter initial quantity"
                  className="modern-input"
                />
              </Form.Item>
            </Col>
          </Row>

          <div className="form-alerts">
            {addModalError && (
              <Alert 
                message={addModalError} 
                type="error" 
                showIcon 
                closable 
                onClose={() => setAddModalError('')}
              />
            )}
            {addModalSuccess && (
              <Alert 
                message={addModalSuccess} 
                type="success" 
                showIcon 
              />
            )}
          </div>

          <Button 
            type="primary" 
            htmlType="submit" 
            className="submit-btn"
            block
            loading={loading}
          >
            Add Product
          </Button>
        </Form>
      </Modal>

      {/* Edit Product Modal */}
      <Modal
        title={<span className="modal-title">Edit Product</span>}
        open={showEditModal}
        onCancel={() => {
          setShowEditModal(false);
          setEditingProduct(null);
        }}
        footer={null}
        className="modern-modal"
        width={600}
        destroyOnClose
      >
        {editingProduct && (
          <Form
            {...formItemLayout}
            form={editForm}
            onFinish={handleUpdateProduct}
          >
            {/* Product Name */}
            <Row gutter={24}>
              <Col span={24}>
                <Form.Item
                  label="Product Name"
                  name="type"
                  rules={[{ required: true, message: 'Please enter product name' }]}
                >
                  <Input
                    className="modern-input"
                  />
                </Form.Item>
              </Col>
            </Row>

            {/* Category */}
            <Row gutter={24}>
              <Col span={24}>
                <Form.Item
                  label="Category"
                  name="category_id"
                  rules={[{ required: true, message: 'Please select a category' }]}
                >
                  <Select
                    className="modern-input"
                  >
                    {categories.map(category => (
                      <Select.Option key={category.id} value={category.id.toString()}>
                        {category.name}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            </Row>

            {/* Price and Size in one row */}
            <Row gutter={24}>
              <Col span={12}>
                <Form.Item
                  label="Price (ETB)"
                  name="price"
                  rules={[
                    { validator: validatePrice }
                  ]}
                >
                  <Input
                    className="modern-input"
                    type="number"
                    step="0.01"
                    min="0"
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label="Size"
                  name="size"
                  rules={[{ required: true, message: 'Please enter product size' }]}
                >
                  <Input
                    className="modern-input"
                  />
                </Form.Item>
              </Col>
            </Row>

            {/* Current Quantity and Add Quantity */}
            <Row gutter={24}>
              <Col span={12}>
                <Form.Item label="Current Quantity">
                  <Input
                    value={editingProduct?.quantity || 0}
                    disabled
                    className="modern-input"
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="Add Quantity">
                  <Input
                    type="number"
                    min="0"
                    value={addedQuantity}
                    onChange={(e) => setAddedQuantity(parseInt(e.target.value) || 0)}
                    className="modern-input"
                    placeholder="Add to current"
                  />
                </Form.Item>
              </Col>
            </Row>

            <div className="form-alerts">
              {editModalError && (
                <Alert 
                  message={editModalError} 
                  type="error" 
                  showIcon 
                  closable 
                  onClose={() => setEditModalError('')}
                />
              )}
              {editModalSuccess && (
                <Alert 
                  message={editModalSuccess} 
                  type="success" 
                  showIcon 
                />
              )}
            </div>

            <Button 
              type="primary" 
              htmlType="submit" 
              className="submit-btn"
              block
              loading={loading}
            >
              Update Product
            </Button>
          </Form>
        )}
      </Modal>
    </div>
  );
}

export default ProductsPage;